import type { ReactNode } from 'react';

import { describeStatus, type StatusChipKind, type StatusMarker } from './statusLooks';

/** The tones a badge may take; each maps to a `--tone-*` triple. */
export type BadgeTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger' | 'ai';

export interface BadgeProps {
  /** Defaults to `neutral`. */
  readonly tone?: BadgeTone;
  /** Draws it as an outline rather than a fill, for dense rows. */
  readonly subtle?: boolean;
  /** The label. */
  readonly children: ReactNode;
}

/**
 * A small label: a count, a role, a category.
 *
 * A badge carries no state of its own and is never the only place a piece of
 * information appears. Anything that *is* a state belongs in
 * {@link StatusChip}, which owes the extra guarantees that come with encoding
 * one.
 *
 * @param props see {@link BadgeProps}
 * @returns the badge
 */
export function Badge({
  tone = 'neutral',
  subtle = false,
  children,
}: BadgeProps): React.JSX.Element {
  return (
    <span className="fo-badge" data-tone={tone} data-subtle={subtle ? '' : undefined}>
      {children}
    </span>
  );
}

const MARKER_PATHS: Record<StatusMarker, React.JSX.Element> = {
  ring: <circle cx="6" cy="6" r="4" fill="none" strokeWidth="2" />,
  dot: <circle cx="6" cy="6" r="4.5" stroke="none" />,
  chevron: <path d="M3.5 2 8 6l-4.5 4" fill="none" strokeWidth="2" strokeLinecap="round" />,
  square: <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="none" />,
  check: <path d="M2 6.4 5 9.4 10 2.8" fill="none" strokeWidth="2" strokeLinecap="round" />,
  pause: (
    <>
      <rect x="2.5" y="2" width="2.6" height="8" rx="1" stroke="none" />
      <rect x="6.9" y="2" width="2.6" height="8" rx="1" stroke="none" />
    </>
  ),
  slash: <path d="M2.5 9.5 9.5 2.5" fill="none" strokeWidth="2" strokeLinecap="round" />,
  funnel: <path d="M1.8 2.2h8.4L7.2 6.2v3.6L4.8 8.4V6.2Z" stroke="none" />,
};

export interface StatusChipProps {
  /** Which vocabulary `value` belongs to. */
  readonly kind: StatusChipKind;
  /** The code the server sent; one the interface has no wording for still reads as words. */
  readonly value: string;
}

/**
 * A lifecycle state, drawn so it survives being printed in black and white.
 *
 * Every state carries three independent channels: a colour, a distinct marker
 * shape, and a word. WCAG 1.4.1 forbids colour being the only one, and on a
 * dispatch board that rule is not a formality — the board is read at a glance,
 * often on a laptop in a van in daylight, sometimes by someone with a colour
 * vision deficiency, and "teal vs. green" is not a distinction anyone should
 * have to make under those conditions.
 *
 * The word always comes from the shared vocabulary, so a status reads the same
 * on every screen. A pipeline state also names its phase, because fourteen
 * states are too many to scan but seven phases are not.
 *
 * @param props see {@link StatusChipProps}
 * @returns the chip
 */
export function StatusChip({ kind, value }: StatusChipProps): React.JSX.Element {
  const { label, look, phase } = describeStatus(kind, value);

  return (
    <span className="fo-status" data-family={kind} data-tone={look.tone} data-state={look.token}>
      <svg
        className="fo-status__marker"
        viewBox="0 0 12 12"
        width="0.75em"
        height="0.75em"
        fill="currentColor"
        stroke="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        {MARKER_PATHS[look.marker]}
      </svg>
      {phase === undefined ? (
        label
      ) : (
        <>
          <span className="fo-status__phase">{phase}</span>
          {` · ${label}`}
        </>
      )}
    </span>
  );
}
